<div class="p-4 max-w-7xl mx-auto space-y-4" *ngIf="batch">

  <!-- ============================================================ -->
  <!-- Header bar                                                    -->
  <!-- ============================================================ -->
  <div class="flex items-center justify-between gap-3 flex-wrap">
    <button type="button" class="btn btn-ghost" (click)="backToAudit()">
      <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" class="w-4 h-4">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
      </svg>
      Volver a Auditoría
    </button>
    <span class="pill pill-neutral font-mono">{{ batch.id }}</span>
  </div>

  <!-- ============================================================ -->
  <!-- Title                                                         -->
  <!-- ============================================================ -->
  <div>
    <h1 class="text-2xl font-bold text-slate-900 tracking-tight">
      Corrección masiva
    </h1>
    <p class="text-sm text-slate-500 mt-1">
      {{ batch.findingType }}
      <span class="text-slate-400">·</span>
      <span class="capitalize">{{ batch.scope }}</span>
    </p>
  </div>

  <!-- ============================================================ -->
  <!-- Status hero card                                              -->
  <!-- ============================================================ -->
  <div class="status-card"
    [class.is-running]="batch.status === 'Running'"
    [class.is-completed]="batch.status === 'Completed'"
    [class.is-failed]="batch.status === 'Failed'"
    [class.is-cancelled]="batch.status === 'Cancelled'">

    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0 space-y-2 flex-1">
        <div class="flex items-center gap-2 flex-wrap">
          <ng-container [ngSwitch]="batch.status">
            <span *ngSwitchCase="'Running'" class="pill pill-sky">
              <span class="spinner-dot"></span>
              Procesando
            </span>
            <span *ngSwitchCase="'Completed'" class="pill pill-emerald">
              <i class="fas fa-check"></i> Completado
            </span>
            <span *ngSwitchCase="'Failed'" class="pill pill-rose">
              <i class="fas fa-exclamation-triangle"></i> Con errores
            </span>
            <span *ngSwitchCase="'Cancelled'" class="pill pill-amber">
              <i class="fas fa-ban"></i> Cancelado
            </span>
          </ng-container>
          <span class="text-sm font-semibold text-slate-900 tabular-nums">
            {{ batch.totalItems }} operaciones
          </span>
        </div>

        <!-- Stats inline -->
        <div class="flex items-center gap-4 text-xs tabular-nums">
          <span class="text-emerald-700"><strong>{{ batch.successCount }}</strong> exitosas</span>
          <span class="text-rose-700"><strong>{{ batch.failedCount }}</strong> fallidas</span>
          <span class="text-slate-600"><strong>{{ batch.skippedCount }}</strong> omitidas</span>
        </div>

        <!-- Progress bar -->
        <div class="progress-track"
          [class.is-running]="batch.status === 'Running'"
          *ngIf="batch.totalItems > 0">
          <div class="progress-segment seg-success"
            [style.width.%]="(batch.successCount / batch.totalItems) * 100"></div>
          <div class="progress-segment seg-failed"
            [style.width.%]="(batch.failedCount / batch.totalItems) * 100"></div>
          <div class="progress-segment seg-skipped"
            [style.width.%]="(batch.skippedCount / batch.totalItems) * 100"></div>
        </div>

        <!-- Timestamps + comment -->
        <div class="text-xs text-slate-500 space-y-0.5">
          <div>
            <span class="text-slate-400">Inicio:</span>
            {{ batch.startedAt | date:'short' }}
            <ng-container *ngIf="batch.finishedAt">
              <span class="mx-1 text-slate-400">·</span>
              <span class="text-slate-400">Fin:</span>
              {{ batch.finishedAt | date:'short' }}
            </ng-container>
          </div>
          <div class="italic text-slate-600 max-w-3xl">"{{ batch.comment }}"</div>
        </div>
      </div>

      <!-- Cancelar visible solo si Running -->
      <button *ngIf="isRunning"
        type="button" class="btn btn-danger shrink-0" (click)="cancel()">
        <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
        </svg>
        Cancelar
      </button>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- Items section                                                 -->
  <!-- ============================================================ -->
  <div class="bg-white border border-slate-200 rounded-xl overflow-hidden">
    <div class="px-5 py-3 border-b border-slate-200 flex items-center gap-3 flex-wrap">
      <h2 class="text-base font-semibold text-slate-900">Operaciones</h2>

      <div class="flex items-center gap-2 ml-auto">
        <label class="text-xs text-slate-500">Filtro:</label>
        <select [(ngModel)]="itemFilter"
          class="border border-slate-300 rounded-md px-2 py-1 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          <option value="all">Todas ({{ batch.items.length }})</option>
          <option value="failed">Solo fallidas ({{ batch.failedCount }})</option>
          <option value="pending">Pending / Running</option>
        </select>
        <span class="text-xs text-slate-500 tabular-nums">
          {{ filteredItems.length }} mostradas
        </span>
      </div>
    </div>

    <!-- Empty state -->
    <div *ngIf="filteredItems.length === 0" class="bulk-empty">
      <svg class="empty-icon" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round"
          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>
      <p>Sin operaciones para este filtro.</p>
    </div>

    <!-- Table -->
    <div *ngIf="filteredItems.length > 0" class="overflow-x-auto">
      <table class="bulk-items-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Target</th>
            <th>Estado</th>
            <th class="num">Originales</th>
            <th class="num">Afectadas</th>
            <th class="num">ms</th>
            <th>Error</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let i of filteredItems">
            <td class="num text-slate-400">{{ i.id }}</td>
            <td class="mono">{{ formatTarget(i) }}</td>
            <td>
              <span [ngSwitch]="i.status">
                <span *ngSwitchCase="'Pending'" class="pill pill-neutral">Pending</span>
                <span *ngSwitchCase="'Running'" class="pill pill-sky">
                  <span class="spinner-dot"></span> Running
                </span>
                <span *ngSwitchCase="'Completed'" class="pill pill-emerald">✓ Completed</span>
                <span *ngSwitchCase="'Failed'" class="pill pill-rose">⚠ Failed</span>
                <span *ngSwitchCase="'Skipped'" class="pill pill-amber">⊘ Skipped</span>
              </span>
            </td>
            <td class="num">{{ i.originalFindingsCount }}</td>
            <td class="num">{{ i.affectedRows ?? '—' }}</td>
            <td class="num text-slate-400">{{ i.durationMs ?? '—' }}</td>
            <td class="error" [title]="i.error || ''">{{ i.error || '' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- Footer (solo cuando NO Running)                              -->
  <!-- ============================================================ -->
  <div *ngIf="!isRunning" class="bulk-footer">
    <button type="button" class="btn btn-ghost" (click)="downloadCsv()">
      <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" class="w-4 h-4">
        <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"/>
      </svg>
      Descargar CSV
    </button>
    <button type="button" class="btn btn-primary" (click)="backToAudit()">
      Volver a Auditoría
    </button>
  </div>

</div>

<!-- Loading inicial -->
<div *ngIf="loading && !batch" class="p-8 text-center text-slate-500 text-sm">
  Cargando batch…
</div>
